import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IdCard, ShieldCheck, Wallet } from 'lucide-react';
import type { PayerPlanDto } from '@his/shared';
import { get, post, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, DatePicker, Field, FormSkeleton, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';
import { safeReturnTo } from '../opd/BookAppointmentPage';

export interface BenefitRow {
  category: string;
  coPayPct: number | null;
  coPayFixedMinor: number | null;
  coPayMaxMinor: number | null;
  approvalLimitMinor: number | null;
  allowedMinor: number | null;
  usedMinor: number | null;
  maxPeriod: string | null;
}

export interface CoverageRow {
  id: string;
  payerId: string;
  payer: { id: string; name: string };
  plan: { id: string; code: string; name: string } | null;
  policyNo: string;
  employeeId: string | null;
  relation: string;
  memberCategory: string | null;
  validFrom: string | null;
  validTo: string | null;
  coPayPct: number | null;
  annualLimitMinor: number | null;
  consumedMinor: number;
  isActive: boolean;
  verifiedAt: string | null;
  externalEligibilityRef: string | null;
  externalEligibilityDate: string | null;
  source: string | null;
  eligibilityAt?: string | null;
  eligibilityOutcome?: string | null;
  inForce?: boolean | null;
  network?: string | null;
  benefits?: BenefitRow[];
}

interface CoverageForm {
  payerId: string;
  planId: string;
  policyNo: string;
  employeeId: string;
  relation: string;
  memberCategory: string;
  validFrom: string;
  validTo: string;
  coPayPct: string;
  annualLimit: string;
}

const EMPTY: CoverageForm = { payerId: '', planId: '', policyNo: '', employeeId: '', relation: 'self', memberCategory: '', validFrom: '', validTo: '', coPayPct: '', annualLimit: '' };

function formOf(row: CoverageRow | null): CoverageForm {
  if (!row) return EMPTY;
  return {
    payerId: row.payerId,
    planId: row.plan?.id ?? '',
    policyNo: row.policyNo,
    employeeId: row.employeeId ?? '',
    relation: row.relation,
    memberCategory: row.memberCategory ?? '',
    validFrom: row.validFrom ? row.validFrom.slice(0, 10) : '',
    validTo: row.validTo ? row.validTo.slice(0, 10) : '',
    coPayPct: row.coPayPct === null ? '' : String(row.coPayPct),
    annualLimit: row.annualLimitMinor === null ? '' : formatMoneyInput(row.annualLimitMinor),
  };
}

export function CoveragePage() {
  const { patientId = '', coverageId } = useParams<{ patientId: string; coverageId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), `/patients/${patientId}`);
  const coverages = useQuery({
    queryKey: ['patient-coverages', patientId],
    queryFn: () => get<CoverageRow[]>(`/insurance/patients/${patientId}/coverages`),
    enabled: Boolean(coverageId),
  });
  const row = coverageId ? (coverages.data?.find((item) => item.id === coverageId) ?? null) : null;

  if (coverageId && coverages.isLoading) return <FormSkeleton />;
  if (coverageId && !row) return <Alert variant="destructive">{coverages.error ? describeError(coverages.error) : 'This policy is not on file.'}</Alert>;

  return <CoverageEditor key={row?.id ?? 'new'} patientId={patientId} row={row} returnTo={returnTo} />;
}

function CoverageEditor({ patientId, row, returnTo }: { patientId: string; row: CoverageRow | null; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const initial = formOf(row);
  const [form, setForm] = useState<CoverageForm>(initial);
  const [error, setError] = useState<string | null>(null);
  const payers = useQuery({ queryKey: ['payers'], queryFn: () => get<{ id: string; name: string }[]>('/insurance/payers') });
  const plans = useQuery({
    queryKey: ['payer-plans', form.payerId],
    queryFn: () => get<PayerPlanDto[]>(`/insurance/payers/${form.payerId}/plans`),
    enabled: Boolean(form.payerId),
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => {
      const body = {
        policyNo: form.policyNo.trim(),
        employeeId: form.employeeId.trim() || undefined,
        relation: form.relation,
        memberCategory: form.memberCategory.trim() || null,
        planId: form.planId || null,
        validFrom: form.validFrom ? new Date(`${form.validFrom}T00:00:00.000Z`).toISOString() : null,
        validTo: form.validTo ? new Date(`${form.validTo}T00:00:00.000Z`).toISOString() : null,
        coPayPct: form.coPayPct.trim() === '' ? null : Number(form.coPayPct),
        annualLimitMinor: form.annualLimit.trim() === '' ? null : minorFromInput(form.annualLimit),
        isActive: true,
      };
      return row ? put(`/insurance/coverages/${row.id}`, body) : post('/insurance/coverages', { ...body, patientId, payerId: form.payerId });
    },
    onSuccess: () => {
      toast.success('Policy saved');
      void queryClient.invalidateQueries({ queryKey: ['patient-coverages', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(form.payerId) && form.policyNo.trim().length > 0;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={row ? `Policy ${row.policyNo}` : 'Add a policy'}
      description="A second active policy with the same payer replaces the first; what the first had used of its limit is carried over"
      icon={ShieldCheck}
      eyebrow={tr('billing', 'policy')}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'policy')} icon={ShieldCheck}>
        <Field label="Payer" required>
          <Select value={form.payerId} disabled={Boolean(row)} onChange={(e) => setForm({ ...form, payerId: e.target.value, planId: '' })}>
            <option value="">Choose…</option>
            {payers.data?.map((payer) => (
              <option key={payer.id} value={payer.id}>
                {payer.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Plan">
          <Select value={form.planId} onChange={(e) => setForm({ ...form, planId: e.target.value })} disabled={!form.payerId || plans.isError}>
            <option value="">No plan</option>
            {plans.data?.filter((plan) => plan.isActive).map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name}
              </option>
            ))}
          </Select>
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'membership')} icon={IdCard}>
        <Field label="Member number" required>
          <Input value={form.policyNo} onChange={(e) => setForm({ ...form, policyNo: e.target.value })} />
        </Field>
        <Field label="Employee number">
          <Input value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })} />
        </Field>
        <Field label="Relation to the policy holder">
          <ChoiceButtons
            layout="chips"
            value={form.relation}
            onChange={(relation) => setForm({ ...form, relation })}
            options={['self', 'spouse', 'child', 'parent', 'other'].map((value) => ({ value, label: humanize(value, 'relation') }))}
          />
        </Field>
        <Field label="Member category" hint="Grade, rank or member type, as the payer calls it">
          <Input value={form.memberCategory} onChange={(e) => setForm({ ...form, memberCategory: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'cover_and_limits')} icon={Wallet}>
        <Field label="Valid from">
          <DatePicker value={form.validFrom} onChange={(value) => setForm({ ...form, validFrom: value })} />
        </Field>
        <Field label="Valid until">
          <DatePicker value={form.validTo} onChange={(value) => setForm({ ...form, validTo: value })} />
        </Field>
        <Field label="Co-pay % for this policy" hint="Leave empty to use the plan or payer default">
          <Input type="number" min={0} max={100} value={form.coPayPct} onChange={(e) => setForm({ ...form, coPayPct: e.target.value })} />
        </Field>
        <Field label="Annual limit">
          <Input type="text" dir="ltr" inputMode="decimal" value={form.annualLimit} onChange={(e) => setForm({ ...form, annualLimit: e.target.value })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('billing', 'save')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
